<!-- ProviderAnalysisView.vue -->
<template>
  <div class="provider-analysis-dashboard">
    <!-- Header with Key Metrics -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card bg-gradient-primary text-white">
          <div class="card-body">
            <div class="row align-items-center">
              <div class="col-md-8">
                <h4 class="text-white mb-1">Kenya Network Service Providers Analysis</h4>
                <p class="text-white-50 mb-0">
                  Comprehensive analysis of {{ providers.length }} active providers across {{ totalRegions }} regions
                </p>
              </div>
              <div class="col-md-4 text-md-end">
                <div class="d-flex justify-content-md-end gap-3">
                  <div class="text-center">
                    <h3 class="text-white mb-0">{{ totalCoveragePoints }}</h3>
                    <small class="text-white-50">Coverage Points</small>
                  </div>
                  <div class="text-center">
                    <h3 class="text-white mb-0">{{ averageSpeed }}</h3>
                    <small class="text-white-50">Avg Speed (Mbps)</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Provider Performance Cards -->
    <div class="row mb-4">
      <div class="col-12">
        <h5 class="mb-3">Provider Performance Overview</h5>
      </div>
      <div class="col-lg-4 col-md-6 mb-3" v-for="provider in topProviders" :key="provider.name">
        <div class="card h-100 provider-card" :style="`border-left: 4px solid ${getProviderColor(provider.name)}`">
          <div class="card-body">
            <div class="d-flex align-items-center mb-3">
              <img :src="getProviderLogo(provider.name)" width="40" height="40" class="rounded me-3" :alt="provider.name">
              <div class="flex-grow-1">
                <h6 class="mb-1">{{ provider.name }}</h6>
                <div class="d-flex align-items-center">
                  <span class="badge me-2" :style="`background-color: ${getProviderColor(provider.name)}`">
                    {{ getMarketShare(provider) }}% Market Share
                  </span>
                  <div class="text-warning" v-if="provider.avgReliability > 0">
                    {{ '⭐'.repeat(Math.round(provider.avgReliability / 20)) }}
                  </div>
                </div>
              </div>
            </div>

            <div class="row text-center">
              <div class="col-4">
                <h5 class="text-primary mb-0">{{ provider.totalPoints }}</h5>
                <small class="text-muted">Points</small>
              </div>
              <div class="col-4">
                <h5 class="text-success mb-0">{{ provider.avgSpeed }}</h5>
                <small class="text-muted">Mbps</small>
              </div>
              <div class="col-4">
                <h5 class="text-info mb-0" v-if="provider.avgCost > 0">{{ formatCurrency(provider.avgCost) }}</h5>
                <h5 class="text-muted mb-0" v-else>N/A</h5>
                <small class="text-muted">Cost</small>
              </div>
            </div>

            <div class="mt-3">
              <div class="row">
                <div class="col-6">
                  <small class="text-muted">Primary Tech:</small>
                  <div class="fw-bold">{{ getPrimaryTechnology(provider) }}</div>
                </div>
                <div class="col-6">
                  <small class="text-muted">Regions:</small>
                  <div class="fw-bold">{{ Object.keys(provider.regions || {}).length }}</div>
                </div>
              </div>
            </div>

            <div class="mt-3">
              <div class="progress" style="height: 6px;">
                <div class="progress-bar"
                     :style="`width: ${getMarketShare(provider)}%; background-color: ${getProviderColor(provider.name)}`">
                </div>
              </div>
              <small class="text-muted">Market Coverage</small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Comparison Charts -->
    <div class="row mb-4">
      <!-- Market Share Pie Chart -->
      <div class="col-lg-6 col-md-12 mb-4">
        <div class="card h-100">
          <div class="card-header">
            <h6 class="mb-0">Market Share Distribution</h6>
          </div>
          <div class="card-body">
            <canvas id="marketShareChart" height="300"></canvas>
          </div>
        </div>
      </div>

      <!-- Speed vs Cost Scatter -->
      <div class="col-lg-6 col-md-12 mb-4">
        <div class="card h-100">
          <div class="card-header">
            <h6 class="mb-0">Speed vs Cost Analysis</h6>
          </div>
          <div class="card-body">
            <canvas id="speedCostChart" height="300"></canvas>
          </div>
        </div>
      </div>
    </div>

    <!-- Technology Distribution and Regional Coverage -->
    <div class="row mb-4">
      <!-- Technology Distribution -->
      <div class="col-lg-8 col-md-12 mb-4">
        <div class="card h-100">
          <div class="card-header">
            <h6 class="mb-0">Technology Distribution by Provider</h6>
          </div>
          <div class="card-body">
            <canvas id="technologyChart" height="250"></canvas>
          </div>
        </div>
      </div>

      <!-- Performance Metrics -->
      <div class="col-lg-4 col-md-12 mb-4">
        <div class="card h-100">
          <div class="card-header">
            <h6 class="mb-0">Performance Rankings</h6>
          </div>
          <div class="card-body">
            <div class="performance-list">
              <!-- Speed Leaders -->
              <div class="mb-3">
                <h6 class="text-success mb-2">Speed Leaders</h6>
                <div v-for="(provider, index) in speedLeaders" :key="provider.name" class="d-flex align-items-center mb-2">
                  <span class="badge bg-success me-2">{{ index + 1 }}</span>
                  <img :src="getProviderLogo(provider.name)" width="20" height="20" class="rounded me-2">
                  <div class="flex-grow-1">
                    <small class="fw-bold">{{ provider.name }}</small>
                    <div class="text-muted small">{{ provider.avgSpeed }} Mbps</div>
                  </div>
                </div>
              </div>

              <!-- Coverage Leaders -->
              <div class="mb-3">
                <h6 class="text-primary mb-2">Coverage Leaders</h6>
                <div v-for="(provider, index) in coverageLeaders" :key="provider.name" class="d-flex align-items-center mb-2">
                  <span class="badge bg-primary me-2">{{ index + 1 }}</span>
                  <img :src="getProviderLogo(provider.name)" width="20" height="20" class="rounded me-2">
                  <div class="flex-grow-1">
                    <small class="fw-bold">{{ provider.name }}</small>
                    <div class="text-muted small">{{ provider.totalPoints }} points</div>
                  </div>
                </div>
              </div>

              <!-- Value Leaders -->
              <div v-if="valueLeaders.length > 0">
                <h6 class="text-warning mb-2">Best Value</h6>
                <div v-for="(provider, index) in valueLeaders" :key="provider.name" class="d-flex align-items-center mb-2">
                  <span class="badge bg-warning me-2">{{ index + 1 }}</span>
                  <img :src="getProviderLogo(provider.name)" width="20" height="20" class="rounded me-2">
                  <div class="flex-grow-1">
                    <small class="fw-bold">{{ provider.name }}</small>
                    <div class="text-muted small">{{ formatCurrency(provider.avgCost) }}/mo</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Detailed Provider Comparison Table -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h6 class="mb-0">Detailed Provider Comparison</h6>
            <div class="btn-group btn-group-sm">
              <button class="btn btn-outline-primary" @click="sortBy('totalPoints')">
                <i class="mdi mdi-sort-numeric-descending"></i> By Coverage
              </button>
              <button class="btn btn-outline-success" @click="sortBy('avgSpeed')">
                <i class="mdi mdi-speedometer"></i> By Speed
              </button>
              <button class="btn btn-outline-warning" @click="sortBy('avgCost')">
                <i class="mdi mdi-currency-usd"></i> By Cost
              </button>
            </div>
          </div>
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead class="table-light">
                <tr>
                  <th>Provider</th>
                  <th class="text-center">Coverage Points</th>
                  <th class="text-center">Avg Speed</th>
                  <th class="text-center">Avg Cost</th>
                  <th class="text-center">Reliability</th>
                  <th class="text-center">Technologies</th>
                  <th class="text-center">Regions</th>
                  <th class="text-center">Market Share</th>
                  <th class="text-center">Performance Score</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="provider in sortedProviders" :key="provider.name">
                  <td>
                    <div class="d-flex align-items-center">
                      <div class="provider-indicator me-2" :style="`background-color: ${getProviderColor(provider.name)}`"></div>
                      <img :src="getProviderLogo(provider.name)" width="30" height="30" class="rounded me-2">
                      <div>
                        <div class="fw-bold">{{ provider.name }}</div>
                        <small class="text-muted">{{ getProviderType(provider.name) }}</small>
                      </div>
                    </div>
                  </td>
                  <td class="text-center">
                    <span class="badge bg-info">{{ provider.totalPoints }}</span>
                  </td>
                  <td class="text-center">
                    <div class="d-flex align-items-center justify-content-center">
                      <div class="progress me-2" style="width: 50px; height: 6px;">
                        <div class="progress-bar bg-success"
                             :style="`width: ${Math.min(100, (provider.avgSpeed / 500) * 100)}%`"></div>
                      </div>
                      <span>{{ provider.avgSpeed }} Mbps</span>
                    </div>
                  </td>
                  <td class="text-center">
                    <span v-if="provider.avgCost > 0">KES {{ formatNumber(provider.avgCost) }}</span>
                    <span v-else class="text-muted">N/A</span>
                  </td>
                  <td class="text-center">
                    <div v-if="provider.avgReliability > 0" class="d-flex align-items-center justify-content-center">
                      <div class="progress me-2" style="width: 40px; height: 6px;">
                        <div class="progress-bar"
                             :class="`bg-${getReliabilityColor(provider.avgReliability)}`"
                             :style="`width: ${provider.avgReliability}%`"></div>
                      </div>
                      <span>{{ provider.avgReliability }}%</span>
                    </div>
                    <span v-else class="text-muted">N/A</span>
                  </td>
                  <td class="text-center">
                    <div class="d-flex flex-wrap justify-content-center gap-1">
                        <span v-for="(count, tech) in provider.connectionTypes" :key="tech"
                              class="badge"
                              :class="`bg-${getTechnologyColor(tech)}`">
                          {{ tech }}
                        </span>
                    </div>
                  </td>
                  <td class="text-center">
                    <span class="badge bg-secondary">{{ Object.keys(provider.regions || {}).length }}</span>
                  </td>
                  <td class="text-center">
                    <div class="d-flex align-items-center justify-content-center">
                      <div class="progress me-2" style="width: 40px; height: 6px;">
                        <div class="progress-bar bg-primary"
                             :style="`width: ${getMarketShare(provider)}%`"></div>
                      </div>
                      <span>{{ getMarketShare(provider) }}%</span>
                    </div>
                  </td>
                  <td class="text-center">
                    <div class="performance-score">
                      <div class="score-circle" :class="getPerformanceClass(provider)">
                        {{ getPerformanceScore(provider) }}
                      </div>
                    </div>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import Chart from 'chart.js/auto';

export default {
  name: 'ProviderAnalysisView',
  props: {
    providers: { type: Array, default: () => [] },
    points: { type: Array, default: () => [] }
  },
  data() {
    return {
      sortField: 'totalPoints',
      sortDirection: 'desc',
      marketShareChart: null,
      speedCostChart: null,
      technologyChart: null,
      providerColors: {
        'Safaricom': '#00AA44',
        'Zuku (Wananchi Group)': '#FF6600',
        'Airtel Kenya': '#FF0000',
        'Telkom Kenya': '#0066CC',
        'JTL Faiba': '#8B008B',
        'Poa Internet': '#00CCCC',
        'Liquid Telecom': '#FF69B4',
        'Mawingu Networks': '#32CD32'
      }
    }
  },
  computed: {
    totalCoveragePoints() {
      return this.points.length;
    },
    totalRegions() {
      const regions = new Set(this.points.map(p => p.region));
      return regions.size;
    },
    averageSpeed() {
      if (this.points.length === 0) return 0;
      const totalSpeed = this.points.reduce((sum, p) => sum + (parseFloat(p.speed) || 0), 0);
      return (totalSpeed / this.points.length).toFixed(1);
    },
    topProviders() {
      return [...this.providers].sort((a, b) => b.totalPoints - a.totalPoints).slice(0, 6);
    },
    sortedProviders() {
      return [...this.providers].sort((a, b) => {
        const aVal = a[this.sortField] || 0;
        const bVal = b[this.sortField] || 0;
        return this.sortDirection === 'desc' ? bVal - aVal : aVal - bVal;
      });
    },
    speedLeaders() {
      return [...this.providers]
          .sort((a, b) => parseFloat(b.avgSpeed) - parseFloat(a.avgSpeed))
          .slice(0, 3);
    },
    coverageLeaders() {
      return [...this.providers]
          .sort((a, b) => b.totalPoints - a.totalPoints)
          .slice(0, 3);
    },
    valueLeaders() {
      return [...this.providers]
          .filter(p => p.avgCost > 0)
          .sort((a, b) => parseFloat(a.avgCost) - parseFloat(b.avgCost))
          .slice(0, 3);
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.createCharts();
    });
  },
  methods: {
    createCharts() {
      this.createMarketShareChart();
      this.createSpeedCostChart();
      this.createTechnologyChart();
    },

    createMarketShareChart() {
      const ctx = document.getElementById('marketShareChart');
      if (ctx) {
        const data = this.providers.map(p => p.totalPoints);
        const labels = this.providers.map(p => p.name);
        const colors = this.providers.map(p => this.getProviderColor(p.name));

        this.marketShareChart = new Chart(ctx, {
          type: 'doughnut',
          data: {
            labels: labels,
            datasets: [{
              data: data,
              backgroundColor: colors,
              borderWidth: 2,
              borderColor: '#fff'
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { position: 'bottom' }
            }
          }
        });
      }
    },

    createSpeedCostChart() {
      const ctx = document.getElementById('speedCostChart');
      if (ctx) {
        const data = this.providers.filter(p => p.avgCost > 0).map(p => ({
          x: parseFloat(p.avgCost),
          y: parseFloat(p.avgSpeed),
          label: p.name,
          backgroundColor: this.getProviderColor(p.name)
        }));

        this.speedCostChart = new Chart(ctx, {
          type: 'scatter',
          data: {
            datasets: [{
              label: 'Providers',
              data: data,
              backgroundColor: data.map(d => d.backgroundColor + '80'),
              borderColor: data.map(d => d.backgroundColor),
              pointRadius: 8,
              pointHoverRadius: 10
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              x: { title: { display: true, text: 'Average Cost (KES/month)' } },
              y: { title: { display: true, text: 'Average Speed (Mbps)' } }
            },
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (context) => `${context.raw.label}: KES ${context.raw.x}/mo, ${context.raw.y} Mbps`
                }
              }
            }
          }
        });
      }
    },

    createTechnologyChart() {
      const ctx = document.getElementById('technologyChart');
      if (ctx) {
        const technologies = ['Fiber', '4G', '5G', 'Wireless', 'Satellite'];
        const datasets = this.providers.map(provider => ({
          label: provider.name,
          data: technologies.map(tech => provider.connectionTypes[tech] || 0),
          backgroundColor: this.getProviderColor(provider.name) + '80',
          borderColor: this.getProviderColor(provider.name),
          borderWidth: 1
        }));

        this.technologyChart = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: technologies,
            datasets: datasets
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              x: { stacked: true },
              y: { stacked: true }
            }
          }
        });
      }
    },

    getProviderColor(name) {
      return this.providerColors[name] || '#666666';
    },

    getProviderLogo(name) {
      const logos = {
        'Safaricom': 'assets/images/providers/safaricom-logo.png',
        'Zuku (Wananchi Group)': 'assets/images/providers/zuku-logo.png',
        'Airtel Kenya': 'assets/images/providers/airtel-logo.png',
        'Telkom Kenya': 'assets/images/providers/telkom-logo.png',
        'JTL Faiba': 'assets/images/providers/faiba-logo.png',
        'Poa Internet': 'assets/images/providers/poa-logo.png',
        'Liquid Telecom': 'assets/images/providers/liquid-logo.png',
        'Mawingu Networks': 'assets/images/providers/mawingu-logo.png'
      };
      return logos[name] || 'assets/images/providers/default-provider.png';
    },

    getProviderType(name) {
      const types = {
        'Safaricom': 'Mobile & Fiber Leader',
        'Zuku (Wananchi Group)': 'Cable & Fiber',
        'Airtel Kenya': 'International Mobile',
        'Telkom Kenya': 'Government Telecom',
        'JTL Faiba': 'Fiber Specialist',
        'Poa Internet': 'Community WiFi',
        'Liquid Telecom': 'Enterprise Solutions',
        'Mawingu Networks': 'Rural Specialist'
      };
      return types[name] || 'Network Provider';
    },

    getMarketShare(provider) {
      const total = this.providers.reduce((sum, p) => sum + p.totalPoints, 0);
      return total > 0 ? ((provider.totalPoints / total) * 100).toFixed(1) : 0;
    },

    getPrimaryTechnology(provider) {
      if (!provider.connectionTypes) return 'N/A';
      const entries = Object.entries(provider.connectionTypes);
      if (entries.length === 0) return 'N/A';
      entries.sort((a, b) => b[1] - a[1]);
      return entries[0][0];
    },

    getPerformanceScore(provider) {
      // Calculate performance score based on speed, coverage, and reliability
      const speedScore = Math.min(100, (parseFloat(provider.avgSpeed) / 500) * 100);
      const coverageScore = Math.min(100, (provider.totalPoints / 50) * 100);
      const reliabilityScore = parseFloat(provider.avgReliability) || 0;

      const totalScore = (speedScore + coverageScore + reliabilityScore) / 3;
      return Math.round(totalScore);
    },

    getPerformanceClass(provider) {
      const score = this.getPerformanceScore(provider);
      if (score >= 80) return 'score-excellent';
      if (score >= 60) return 'score-good';
      if (score >= 40) return 'score-average';
      return 'score-poor';
    },

    getReliabilityColor(reliability) {
      if (reliability >= 95) return 'success';
      if (reliability >= 85) return 'warning';
      return 'danger';
    },

    getTechnologyColor(tech) {
      const colors = {
        'Fiber': 'primary',
        '4G': 'success',
        '5G': 'info',
        'Wireless': 'warning',
        'Satellite': 'secondary'
      };
      return colors[tech] || 'secondary';
    },

    formatCurrency(amount) {
      return `KES ${this.formatNumber(amount)}`;
    },

    formatNumber(num) {
      return new Intl.NumberFormat().format(Math.round(num));
    },

    sortBy(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'desc' ? 'asc' : 'desc';
      } else {
        this.sortField = field;
        this.sortDirection = 'desc';
      }
    }
  },

  beforeDestroy() {
    if (this.marketShareChart) this.marketShareChart.destroy();
    if (this.speedCostChart) this.speedCostChart.destroy();
    if (this.technologyChart) this.technologyChart.destroy();
  }
}
</script>